import { ReactElement, useMemo } from "react";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import { MenuItemProps } from "@kaltura/ds-react-components";
import { Download24Icon, Mail24Icon } from "@kaltura/ds-react-icons";
import { baseUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { AddToCalendarOptionsType, ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { useButtonAnalytics, useEventHandler } from "@kaltura/mediaspace-shared-hooks";
import { AddToCalendarMenuItem } from "./AddToCalendarMenuItem";

export interface AddToCalendarMenuItemsProps {
    entryId: string;
    categoryId?: number;
    color?: "default" | "white";
    analyticsValue: { location: string; module?: string };
    onItemClicked?: (itemType: AddToCalendarOptionsType) => void;
    ks?: string;
    calendarEntriesToken?: string;
}

export const useAddToCalendarMenuItems = ({
    entryId,
    categoryId,
    onItemClicked,
    color = "default",
    analyticsValue,
    ks,
    calendarEntriesToken,
}: AddToCalendarMenuItemsProps) => {
    const { showToast } = useToastsContext();
    const config = useConfig();
    const showEmailOption = config.calendar?.showEmailOption ?? false;
    const calendarAppointmentUrl = config.calendar?.getAppointmentUrl;

    const sendCalendarEmail = async () => {
        const url = "/default/calendar/send-calendar-email";

        try {
            const result = await postKmsData(`${baseUrl}${url}`, { entryId, ks });
            if (result.success) {
                showToast({ severity: "success", message: result.message });
                onItemClicked?.(AddToCalendarOptionsType.EMAIL);
            }
            else {
                if (result.redirect) {
                    window.location.href = `${result.redirect}?ref=${baseUrl}${encodeURIComponent(window.location.pathname + window.location.search)}`;
                    return;
                }
                showToast({ severity: "error", message: result.error });
            }
        }
        catch (e) {
            showToast({ severity: "error", message: translate("Error sending calendar email") });
        }
    };

    const sendButtonAnalytics = useButtonAnalytics();
    const handleMenuItemClick = useEventHandler((type: AddToCalendarOptionsType) => {
        const buttonType =
            type === AddToCalendarOptionsType.EMAIL ? ButtonClickAnalyticsType.SEND : ButtonClickAnalyticsType.DOWNLOAD;
        sendButtonAnalytics(
            `Add to calendar - ${analyticsValue.location} - ${type}${analyticsValue.module ? ` - ${analyticsValue.module}` : ""}`,
            buttonType,
            entryId
        );

        if (type !== AddToCalendarOptionsType.EMAIL) {
            onItemClicked?.(type);
            let params = "/entryid/" + entryId + (categoryId ? `/categoryId/${categoryId}` : "") + "/cal/" + type;
            if (calendarEntriesToken?.length) {
                params = `${params}/entryTokens/${calendarEntriesToken}`;
            }
            window.open(baseUrl + calendarAppointmentUrl + params);
        }
        else {
            sendCalendarEmail();
        }
    });

    return useMemo(
        () =>
            [
                showEmailOption && (
                    <AddToCalendarMenuItem
                        key={"send-by-email"}
                        component="a"
                        color={color}
                        onClick={() => handleMenuItemClick(AddToCalendarOptionsType.EMAIL)}
                    >
                        <Mail24Icon /> {translate("Send by email")}
                    </AddToCalendarMenuItem>
                ),
                <AddToCalendarMenuItem
                    key={"outlook"}
                    component="a"
                    color={color}
                    onClick={() => handleMenuItemClick(AddToCalendarOptionsType.OUTLOOK)}
                >
                    <Download24Icon /> {translate("Outlook calendar")}
                </AddToCalendarMenuItem>,
                <AddToCalendarMenuItem
                    key={"google"}
                    component="a"
                    color={color}
                    onClick={() => handleMenuItemClick(AddToCalendarOptionsType.GOOGLE)}
                >
                    <Download24Icon /> {translate("Google calendar")}
                </AddToCalendarMenuItem>,
            ].filter(Boolean) as ReactElement<MenuItemProps>[],
        [color, handleMenuItemClick, showEmailOption]
    );
};
